import moment from 'moment';

import storageManager from './storageManager';

interface ILastMarkDateConfig {
  userId: number;
  modifiedDate: number;
  selectedDate: number | null;
}

export const localStorageLastMarkDateKey = 'LAST_MARK_DATE';

/**
 * Pobranie konfiguracji ostatniej daty dla ocen dla wszystkich użytkowników z local storage
 *
 */
const getLastMarkDateListConfig = (): ILastMarkDateConfig[] | null => {
  const lastMarkDateConfigStorage = storageManager.get(localStorageLastMarkDateKey);

  let lastMarkDateConfigList: ILastMarkDateConfig[] | null = null;

  if (lastMarkDateConfigStorage) {
    try {
      lastMarkDateConfigList = JSON.parse(lastMarkDateConfigStorage);
    } catch {
      storageManager.remove(localStorageLastMarkDateKey);
    }
  }

  return lastMarkDateConfigList;
};

/**
 * Pobranie konfiguracji ostatniej daty dla ocen dla wskazanego użytkownika z local storage
 *
 * @param userId
 */
export const getLastMarkDateConfig = (userId: number): ILastMarkDateConfig | undefined => {
  const lastMarkDateConfigList = getLastMarkDateListConfig();
  const lastMarkDateConfig = lastMarkDateConfigList?.find(config => config.userId === userId);
  const validatedLastMarkDateConfig = validateDataFromStorage(lastMarkDateConfig);

  if (validatedLastMarkDateConfig) {
    if (checkDateIsInWeek(validatedLastMarkDateConfig.modifiedDate)) {
      return validatedLastMarkDateConfig;
    } else {
      removeLastMarkDateUserConfig(userId);
    }
  }

  return undefined;
};

/**
 * Wstawianie konfiguracji do local storage
 *
 */
const setLastMarkDateConfigListToStorage = (data: ILastMarkDateConfig): void => {
  const lastMarkDateConfigList = getLastMarkDateListConfig();

  // jeśli w konfiguracji jest już podany user to podmień konfiguracje
  if (lastMarkDateConfigList?.find(config => config.userId === data.userId)) {
    const newStorageData = lastMarkDateConfigList.map(config => (config.userId === data.userId ? data : config));
    storageManager.set(localStorageLastMarkDateKey, `${JSON.stringify(newStorageData)}`);
  } else if (lastMarkDateConfigList) {
    // jeśli nie ma to dodaj jego konfuracje
    const newStorageData = [...lastMarkDateConfigList, data];
    storageManager.set(localStorageLastMarkDateKey, `${JSON.stringify(newStorageData)}`);
  } else {
    // jeśli nie ma kofiguracji to stwórz ją
    storageManager.set(localStorageLastMarkDateKey, `${JSON.stringify([data])}`);
  }
};

/**
 * Ustawienie ostatniej daty wybranej przy wystawianiu oceny
 *
 */
export const setLastMarkDateUserConfig = (userId: number, selectedDate: number): void => {
  const changedLastMarkDateUserConfig = {
    userId,
    modifiedDate: moment().unix(),
    selectedDate: selectedDate,
  };

  setLastMarkDateConfigListToStorage(changedLastMarkDateUserConfig);
};

/**
 * Funkcja usuwająca ostatnio wybraną datę przy wskazanym użytkowniku
 *
 * @param userId
 */
const removeLastMarkDateUserConfig = (userId: number): void => {
  const lastMarkDateConfigList = getLastMarkDateListConfig();

  if (lastMarkDateConfigList) {
    const newStorageData = lastMarkDateConfigList.map(config =>
      config.userId === userId ? { ...config, selectedDate: null } : config,
    );
    storageManager.set(localStorageLastMarkDateKey, `${JSON.stringify(newStorageData)}`);
  }
};

/**
 * Funkcja sprawdzająca czy data ostatniego dostępu była dzisiaj
 *
 * @param date
 */
const checkDateIsInWeek = (date: number): boolean => {
  const today = moment();
  const modifiedDate = moment.unix(date);

  return today.diff(modifiedDate, 'weeks') < 1;
};

/**
 * Funkcja sprawdzająca czy dane z local storage są oraz czy są poprawne
 *
 * @param lastMarkDateConfig
 */
const validateDataFromStorage = (lastMarkDateConfig: ILastMarkDateConfig | undefined): ILastMarkDateConfig | undefined => {
  if (
    lastMarkDateConfig &&
    (typeof lastMarkDateConfig.userId !== 'string' ||
      !lastMarkDateConfig.modifiedDate ||
      typeof lastMarkDateConfig.modifiedDate !== 'number' ||
      (typeof lastMarkDateConfig.selectedDate !== 'number' && lastMarkDateConfig.selectedDate !== null))
  ) {
    storageManager.remove(localStorageLastMarkDateKey);
    return undefined;
  }

  return lastMarkDateConfig;
};
